"use client";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ChangeEvent, useEffect, useState } from "react";
import SalifyLink from "src/lib/components/link/salify-link";
import styles from "./checkout-card.module.css";
import { SelectItem } from "./common";
import { formatMoney } from "src/lib/utils/formatter";
import SalifyFallback from "src/lib/components/salify-fallback/default";

export interface CheckoutCardDefaultProps {
  total: number;
  provinceOptions: SelectItem[];
  selectedProvince?: string; // SelectItem.value
  districtOptions: SelectItem[];
  selectedDistrict?: string; // SelectItem.value
  wardOptions: SelectItem[];
  selectedWard?: string; // SelectItem.value
  address?: string;
  output?: (province?: string, district?: string, ward?: string) => void;
  onCalculateShipping?: (
    province?: string,
    district?: string,
    ward?: string,
    address?: string,
  ) => void;
  onNextStep?: () => void;
  allowAddVoucher?: boolean;
  showSippingEstimate?: boolean;
}

const CheckoutCardDefault = (props: CheckoutCardDefaultProps) => {
  const {
    total,
    provinceOptions,
    selectedProvince,
    districtOptions,
    selectedDistrict,
    wardOptions,
    selectedWard,
    address,
    output,
  } = props;
  const [province, setProvince] = useState(selectedProvince);
  const [district, setDistrict] = useState(selectedDistrict);
  const [ward, setWard] = useState(selectedWard);
  const [adrs, setAdrs] = useState(address);

  const onChangeProvinceOption = (event: SelectChangeEvent) => {
    setProvince(event.target.value as string);
  };

  const onChangeDistrictOption = (event: SelectChangeEvent) => {
    setDistrict(event.target.value as string);
  };

  const onChangeWardOption = (event: SelectChangeEvent) => {
    setWard(event.target.value as string);
  };

  const onChangeAddress = (event: ChangeEvent<HTMLInputElement>) => {
    setAdrs(event.target.value);
  };

  useEffect(() => {
    if (output) {
      output(province, district, ward);
    }
  }, [province, district, ward]);

  const renderVoucher = () => {
    if (!props.allowAddVoucher) return null;
    return (
      <>
        <FormControl size="small" fullWidth sx={{ margin: "0 0 16px" }}>
          <TextField label="Voucher" placeholder="Voucher" size="small" />
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          sx={{ textTransform: "none", fontWeight: "bold", margin: "0 0 16px" }}
        >
          <SalifyFallback.Label path="applyVoucherButton" default="Apply voucher" />
        </Button>
        <Divider light sx={{ margin: "0 0 16px" }} />
      </>
    );
  };

  const renderShippingEst = () => {
    if (!props.showSippingEstimate) return null;
    return (
      <>
        <Box className={styles.boxTitle}>Shipping Estimates</Box>
        <Divider light sx={{ margin: "0 0 16px" }} />
        <FormControl size="small" fullWidth sx={{ margin: "0 0 16px" }}>
          <InputLabel>
            <SalifyFallback.Label path="provinceLabel" default="Thành phố/Tỉnh" />
          </InputLabel>
          <Select
            onChange={onChangeProvinceOption}
            value={province}
            label="Thành phố/Tỉnh"
            //placeholder="Chọn Thành phố/Tỉnh"
          >
            {provinceOptions.map((option, idx) => (
              <MenuItem value={option.value} key={`provinceOption${idx}`}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth sx={{ margin: "0 0 16px" }}>
          <InputLabel>
            <SalifyFallback.Label path="districtLabel" default="Quận/Huyện" />
          </InputLabel>
          <Select
            onChange={onChangeDistrictOption}
            value={district}
            label="Quận/Huyện"
            //placeholder="Chọn Quận/Huyện"
          >
            {districtOptions.map((option, idx) => (
              <MenuItem value={option.value} key={`districtOption${idx}`}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth sx={{ margin: "0 0 16px" }}>
          <InputLabel>
            <SalifyFallback.Label path="wardLabel" default="Phường/Xã" />
          </InputLabel>
          <Select
            onChange={onChangeWardOption}
            value={ward}
            label="Phường/Xã"
            //placeholder="Chọn Phường/Xã"
          >
            {wardOptions.map((option, idx) => (
              <MenuItem value={option.value} key={`wardOption${idx}`}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth sx={{ margin: "0 0 16px" }}>
          <TextField
            label="Số nhà, tên đường..."
            placeholder="Số nhà, tên đường..."
            size="small"
            onChange={onChangeAddress}
            value={adrs}
          />
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          sx={{ textTransform: "none", fontWeight: "bold", margin: "0 0 16px" }}
        >
          <SalifyFallback.Label path="calculateShippingButton" default="Calculate Shipping" />
        </Button>
      </>
    );
  };

  return (
    <Card className={styles.cardBox}>
      <Box className={styles.totalBox}>
        <Typography variant="body1" align="left" sx={{ color: "#4B566B" }}>
          Total
        </Typography>
        <Typography variant="body1" align="right" sx={{ fontWeight: 600 }}>
          {formatMoney(total)}
        </Typography>
      </Box>
      <Divider light sx={{ margin: "0 0 16px" }} />
      <Box className={styles.boxTitle}>
        <span>Ghi chú</span>
        <span className={styles.noteTag}>Note</span>
      </Box>
      <FormControl size="small" fullWidth sx={{ margin: "0 0 16px" }}>
        <TextField multiline rows={6} size="small" />
      </FormControl>
      <Divider light sx={{ margin: "0 0 16px" }} />
      {renderVoucher()}
      {renderShippingEst()}
      <SalifyLink href={"/checkout"}>
        <Button
          variant="contained"
          size="small"
          fullWidth
          sx={{ textTransform: "none", fontWeight: "bold", margin: "0 0 16px" }}
        >
          Checkout Now
        </Button>
      </SalifyLink>
    </Card>
  );
};

CheckoutCardDefault.displayName = "CheckoutCardDefault";

export default CheckoutCardDefault;
